import { useAuth } from "@/context/AuthContext";
import { CloudOff, RefreshCw, ServerCrash } from "lucide-react";

// "Can't reach the server" is NOT "you are logged out". These two surfaces say so out loud:
// a thin self-clearing bar while you work, and a boot screen instead of the login form.
// Sized for gloved hands on a warehouse tablet, not a 10px desktop strip.
export function ServerStatusBar() {
  const { unreachable, retryNow } = useAuth();
  if (!unreachable) return null;
  return (
    <div data-testid="server-unreachable-bar" role="status" aria-live="polite"
      className="sticky top-0 z-50 w-full bg-[#C76714] text-white print:hidden shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <CloudOff className="w-5 h-5 shrink-0 animate-pulse" />
        <span className="text-sm md:text-base font-semibold">Can’t reach the server — retrying…</span>
        <span data-testid="server-unreachable-still-signed-in" className="text-xs md:text-sm text-white/90">
          You are still signed in. Anything you save while this shows will fail loudly, not silently.
        </span>
        <button type="button" data-testid="server-unreachable-retry" onClick={retryNow}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 text-sm font-semibold min-h-[44px] sm:min-h-0">
          <RefreshCw className="w-4 h-4" />Retry now
        </button>
      </div>
    </div>
  );
}

// Boot-time state: the session can't be checked. TWO different reasons, TWO different sentences,
// one component — because the app's own rule is that different facts get different words, and
// "can't reach the server" would be a lie when the server answered with a 500 (R-10).
//   variant="unreachable" — no answer at all, or a gateway code. The /version probe is retrying.
//   variant="unverified"  — the server ANSWERED, with an error, so it never said whether the
//                           session is good. Tried three times already. Still not a sign-out.
// Showing the login form for either would claim the session ended, which is the bug GR-39 and
// R-10 exist to kill.
export function ServerUnreachableScreen({ variant = "unreachable" }) {
  const { retryNow } = useAuth();
  const unverified = variant === "unverified";
  const Icon = unverified ? ServerCrash : CloudOff;
  return (
    <div data-testid={unverified ? "session-unverified-screen" : "server-unreachable-screen"}
      className="min-h-screen flex items-center justify-center p-6 bg-[#F8FAFC]">
      <div className="max-w-md w-full rounded-xl border border-[#C76714]/40 bg-white p-8 text-center shadow-sm">
        <Icon className="w-10 h-10 mx-auto text-[#C76714]" strokeWidth={1.5} />
        <h2 className="font-display text-2xl font-bold mt-4">
          {unverified ? "Couldn’t check your session" : "Can’t reach the server"}
        </h2>
        <p className="text-sm text-slate-600 mt-2">
          {unverified
            ? "The server answered with an error, so it never told us whether you are signed in. You have NOT been signed out and nothing you saved is lost. We already tried three times — this usually clears within a minute of a restart."
            : "This is a connection problem, not a sign-out. We’re retrying on our own — you don’t need to log in again. If it keeps up, check the yard wifi or report the outage."}
        </p>
        <button type="button"
          data-testid={unverified ? "session-unverified-retry" : "server-unreachable-screen-retry"}
          onClick={retryNow}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#B01E28] hover:bg-[#8F1820] transition-colors text-white px-5 py-3 text-sm font-semibold min-h-[44px]">
          <RefreshCw className="w-4 h-4" />Try again now
        </button>
      </div>
    </div>
  );
}
